import React, { ReactNode } from 'react'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { BoardList } from '../../../../components/society/BoardList'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useModal } from '../../../../hooks/useModal'
// eslint-disable-next-line max-len
import { CreateUpdateBoardMemberModal } from '../../../../components/society/BoardList/CreateUpdateBoardMemberModal'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ButtonVariant } from '../../../../components/common/Button'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { IconChoices } from '../../../../components/common/Icon'
import { IllustrationChoices } from '../../../../components/common/Illustration'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { i18nSocietyType } from '../../../../i18n'

export const BoardListView = (): ReactNode => {
  const { translate } = useAppTranslation()
  const authUserId = useAuthenticatedUserId()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('society:board', {
      '{{ societyName }}': society?.name,
    })
  )
  const {
    show: showCreateModal,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModal()

  const isAdmin = isUserAdminInSociety(society, authUserId as string)

  const loading = isLoadingSociety

  const societyBoardMemberList = society?.boardMembersList
  const societyNominatingCommitteeList = society?.nominatingCommitteeList

  const errorView =
    !loading &&
    societyBoardMemberList?.length === 0 &&
    societyNominatingCommitteeList?.length === 0
      ? {
          title: translate('boardListView.emptyState.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          subtitle: translate('boardListView.emptyState.subtitle', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          illustration: IllustrationChoices.EMPTY,
        }
      : undefined

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('boardListView.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        buttons={[
          ...(isAdmin
            ? [
                {
                  variant: ButtonVariant.PRIMARY,
                  onClick: openCreateModal,
                  label: translate('boardListView.addBoardMember', {
                    context: i18nSocietyType(society?.societyTypeDisplayName),
                  }),
                  icon: IconChoices.PLUS_SIGN,
                },
              ]
            : []),
        ]}
        loading={loading}
        errorView={errorView}
      >
        <BoardList />
      </ViewBase>
      {isAdmin && society && (
        <CreateUpdateBoardMemberModal
          society={society}
          show={showCreateModal}
          close={closeCreateModal}
        />
      )}
    </ErrorBoundary>
  )
}
